/**
 * Selection Manager
 * 
 * Handles hover and click selection on 3D objects with outline effects.
 * Uses raycasting for mouse interaction and custom outline shaders.
 */

import * as THREE from 'three';
import type { SelectionConfig, IManager } from '../types';

export interface SelectionManagerConfig {
  selection?: SelectionConfig;
}

export interface SelectionCallbacks {
  onHover?: (mesh: THREE.Mesh | null, partName: string | null) => void;
  onSelect?: (mesh: THREE.Mesh | null, partName: string | null) => void;
}

export class SelectionManager implements IManager {
  private scene: THREE.Scene | null = null;
  private camera: THREE.Camera | null = null;
  private domElement: HTMLElement | null = null;
  private config: SelectionConfig;
  
  // Raycasting
  private raycaster = new THREE.Raycaster();
  private mouse = new THREE.Vector2();
  
  // Selection state
  private selectableMeshes: THREE.Mesh[] = [];
  private hoveredMesh: THREE.Mesh | null = null;
  private selectedMesh: THREE.Mesh | null = null;
  
  // Outline materials
  private outlineMaterial: THREE.ShaderMaterial;
  private outlineObjects: Map<THREE.Mesh, THREE.Mesh> = new Map();
  
  // Callbacks
  private callbacks: SelectionCallbacks = {};
  
  // Animation
  private pulseTime = 0;
  
  // Event handlers (bound for cleanup)
  private boundOnMouseMove: (e: MouseEvent) => void;
  private boundOnClick: (e: MouseEvent) => void;
  
  constructor() {
    this.config = {
      enabled: false,
      hoverColor: '#00ff00',
      selectColor: '#ff6600',
      edgeStrength: 3,
      edgeGlow: 0,
      edgeThickness: 1,
      pulseOnHover: true,
      selectablePrefix: 'part_',
    };
    
    // Create outline shader material
    this.outlineMaterial = this.createOutlineMaterial();
    
    // Bind event handlers
    this.boundOnMouseMove = this.onMouseMove.bind(this);
    this.boundOnClick = this.onClick.bind(this);
  }
  
  /**
   * Create the outline shader material
   */
  private createOutlineMaterial(): THREE.ShaderMaterial {
    return new THREE.ShaderMaterial({
      uniforms: {
        outlineColor: { value: new THREE.Color(this.config.hoverColor) },
        outlineThickness: { value: this.config.edgeThickness ?? 1 },
        opacity: { value: 0.8 },
        pulse: { value: 0 },
      },
      vertexShader: `
        uniform float outlineThickness;
        
        void main() {
          vec3 transformed = position + normal * outlineThickness * 0.02;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(transformed, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 outlineColor;
        uniform float opacity;
        uniform float pulse;
        
        void main() {
          float alpha = opacity + pulse * 0.2;
          gl_FragColor = vec4(outlineColor, alpha);
        }
      `,
      side: THREE.BackSide,
      transparent: true,
      depthTest: false,
    });
  }
  
  /**
   * Initialize selection system
   */
  public async init(scene: THREE.Scene, config: SelectionManagerConfig): Promise<void> {
    this.scene = scene;
    
    if (config.selection) {
      this.config = { ...this.config, ...config.selection };
    }
    
    if (!this.config.enabled) return;
    
    // Find all selectable meshes
    this.findSelectableMeshes();
  }
  
  /**
   * Set camera and DOM element for raycasting
   */
  public setupInteraction(camera: THREE.Camera, domElement: HTMLElement): void {
    this.camera = camera;
    this.domElement = domElement;
    
    if (!this.config.enabled) return;
    
    // Add event listeners
    domElement.addEventListener('mousemove', this.boundOnMouseMove);
    domElement.addEventListener('click', this.boundOnClick);
    
    // Set cursor style
    domElement.style.cursor = 'default';
  }
  
  /**
   * Find all selectable meshes in scene
   */
  private findSelectableMeshes(): void {
    this.selectableMeshes = [];
    const prefix = this.config.selectablePrefix ?? 'part_';
    
    this.scene?.traverse((object) => {
      if (object instanceof THREE.Mesh && object.name.startsWith(prefix)) {
        this.selectableMeshes.push(object);
        
        // Store original material
        object.userData.originalMaterial = object.material;
      }
    });
  }
  
  /**
   * Refresh selectable meshes (call after model changes)
   */
  public refreshSelectables(): void {
    this.clearOutlines();
    this.findSelectableMeshes();
  }
  
  /**
   * Handle mouse move for hover detection
   */
  private onMouseMove(event: MouseEvent): void {
    if (!this.camera || !this.domElement || !this.config.enabled) return;
    
    // Calculate mouse position in normalized device coordinates
    const rect = this.domElement.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    
    // Perform raycast
    this.raycaster.setFromCamera(this.mouse, this.camera);
    const intersects = this.raycaster.intersectObjects(this.selectableMeshes, false);
    
    if (intersects.length > 0) {
      const mesh = intersects[0].object as THREE.Mesh;
      
      if (mesh !== this.hoveredMesh) {
        // Clear previous hover
        this.clearHoverOutline();
        
        // Set new hover
        this.hoveredMesh = mesh;
        this.showHoverOutline(mesh);
        
        // Update cursor
        this.domElement!.style.cursor = 'pointer';
        
        // Callback
        this.callbacks.onHover?.(mesh, mesh.name);
      }
    } else {
      if (this.hoveredMesh) {
        this.clearHoverOutline();
        this.hoveredMesh = null;
        this.domElement!.style.cursor = 'default';
        
        // Callback
        this.callbacks.onHover?.(null, null);
      }
    }
  }
  
  /**
   * Handle click for selection
   */
  private onClick(event: MouseEvent): void {
    if (!this.camera || !this.domElement || !this.config.enabled) return;
    
    // Calculate mouse position
    const rect = this.domElement.getBoundingClientRect();
    this.mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    
    // Perform raycast
    this.raycaster.setFromCamera(this.mouse, this.camera);
    const intersects = this.raycaster.intersectObjects(this.selectableMeshes, false);
    
    if (intersects.length > 0) {
      const mesh = intersects[0].object as THREE.Mesh;
      
      // Toggle selection
      if (mesh === this.selectedMesh) {
        // Deselect
        this.clearSelectionOutline();
        this.selectedMesh = null;
        this.callbacks.onSelect?.(null, null);
      } else {
        // Select new mesh
        this.clearSelectionOutline();
        this.selectedMesh = mesh;
        this.showSelectionOutline(mesh);
        this.callbacks.onSelect?.(mesh, mesh.name);
      }
    } else {
      // Click on empty space - deselect
      if (this.selectedMesh) {
        this.clearSelectionOutline();
        this.selectedMesh = null;
        this.callbacks.onSelect?.(null, null);
      }
    }
  }
  
  /**
   * Show hover outline on mesh
   */
  private showHoverOutline(mesh: THREE.Mesh): void {
    if (mesh === this.selectedMesh) return; // Don't override selection
    
    const outline = this.createOutlineMesh(mesh, this.config.hoverColor ?? '#00ff00');
    this.outlineObjects.set(mesh, outline);
    this.scene?.add(outline);
  }
  
  /**
   * Clear hover outline
   */
  private clearHoverOutline(): void {
    if (this.hoveredMesh && this.hoveredMesh !== this.selectedMesh) {
      const outline = this.outlineObjects.get(this.hoveredMesh);
      if (outline) {
        this.scene?.remove(outline);
        outline.geometry.dispose();
        (outline.material as THREE.Material).dispose();
        this.outlineObjects.delete(this.hoveredMesh);
      }
    }
  }
  
  /**
   * Show selection outline on mesh
   */
  private showSelectionOutline(mesh: THREE.Mesh): void {
    // Clear any existing outline for this mesh
    const existing = this.outlineObjects.get(mesh);
    if (existing) {
      this.scene?.remove(existing);
      existing.geometry.dispose();
      (existing.material as THREE.Material).dispose();
    }
    
    const outline = this.createOutlineMesh(mesh, this.config.selectColor ?? '#ff6600');
    this.outlineObjects.set(mesh, outline);
    this.scene?.add(outline);
  }
  
  /**
   * Clear selection outline
   */
  private clearSelectionOutline(): void {
    if (this.selectedMesh) {
      const outline = this.outlineObjects.get(this.selectedMesh);
      if (outline) {
        this.scene?.remove(outline);
        outline.geometry.dispose();
        (outline.material as THREE.Material).dispose();
        this.outlineObjects.delete(this.selectedMesh);
      }
    }
  }
  
  /**
   * Create outline mesh for a given mesh
   */
  private createOutlineMesh(mesh: THREE.Mesh, color: string): THREE.Mesh {
    const outlineGeometry = mesh.geometry.clone();
    const outlineMaterial = this.outlineMaterial.clone();
    outlineMaterial.uniforms.outlineColor.value = new THREE.Color(color);
    outlineMaterial.uniforms.outlineThickness.value = this.config.edgeThickness ?? 1;
    
    const outlineMesh = new THREE.Mesh(outlineGeometry, outlineMaterial);
    outlineMesh.position.copy(mesh.position);
    outlineMesh.rotation.copy(mesh.rotation);
    outlineMesh.scale.copy(mesh.scale);
    
    // Copy world matrix from original mesh
    mesh.updateMatrixWorld(true);
    outlineMesh.matrixAutoUpdate = false;
    outlineMesh.matrix.copy(mesh.matrixWorld);
    
    outlineMesh.renderOrder = 999;
    outlineMesh.userData.isOutline = true;
    
    return outlineMesh;
  }
  
  /**
   * Clear all outlines
   */
  private clearOutlines(): void {
    this.outlineObjects.forEach((outline) => {
      this.scene?.remove(outline);
      outline.geometry.dispose();
      (outline.material as THREE.Material).dispose();
    });
    this.outlineObjects.clear();
    this.hoveredMesh = null;
    this.selectedMesh = null;
  }
  
  /**
   * Set selection callbacks
   */
  public setCallbacks(callbacks: SelectionCallbacks): void {
    this.callbacks = callbacks;
  }
  
  /**
   * Select a mesh programmatically
   */
  public selectMesh(meshName: string): void {
    const mesh = this.selectableMeshes.find((m) => m.name === meshName);
    if (mesh) {
      this.clearSelectionOutline();
      this.selectedMesh = mesh;
      this.showSelectionOutline(mesh);
      this.callbacks.onSelect?.(mesh, mesh.name);
    }
  }
  
  /**
   * Clear selection
   */
  public clearSelection(): void {
    this.clearSelectionOutline();
    this.selectedMesh = null;
    this.callbacks.onSelect?.(null, null);
  }
  
  /**
   * Get currently selected mesh
   */
  public getSelected(): THREE.Mesh | null {
    return this.selectedMesh;
  }
  
  /**
   * Get currently hovered mesh
   */
  public getHovered(): THREE.Mesh | null {
    return this.hoveredMesh;
  }
  
  /**
   * Enable/disable selection
   */
  public setEnabled(enabled: boolean): void {
    this.config.enabled = enabled;
    
    if (!enabled) {
      this.clearOutlines();
      if (this.domElement) {
        this.domElement.style.cursor = 'default';
      }
    }
  }
  
  /**
   * Update outline colors
   */
  public setColors(hoverColor?: string, selectColor?: string): void {
    if (hoverColor) this.config.hoverColor = hoverColor;
    if (selectColor) this.config.selectColor = selectColor;
  }
  
  /**
   * Update (called every frame)
   */
  public update(deltaTime: number): void {
    if (!this.config.enabled || !this.config.pulseOnHover) return;
    
    // Update pulse animation
    this.pulseTime += deltaTime;
    const pulse = Math.sin(this.pulseTime * 4) * 0.5 + 0.5;
    
    // Update hover outline pulse
    if (this.hoveredMesh && this.hoveredMesh !== this.selectedMesh) {
      const outline = this.outlineObjects.get(this.hoveredMesh);
      if (outline) {
        const material = outline.material as THREE.ShaderMaterial;
        material.uniforms.pulse.value = pulse;
      }
    }
    
    // Update outline positions (in case mesh moved)
    this.outlineObjects.forEach((outline, mesh) => {
      mesh.updateMatrixWorld(true);
      outline.matrix.copy(mesh.matrixWorld);
    });
  }
  
  /**
   * Dispose resources
   */
  public dispose(): void {
    // Remove event listeners
    if (this.domElement) {
      this.domElement.removeEventListener('mousemove', this.boundOnMouseMove);
      this.domElement.removeEventListener('click', this.boundOnClick);
    }
    
    // Clear outlines
    this.clearOutlines();
    
    // Dispose materials
    this.outlineMaterial.dispose();
    
    this.scene = null;
    this.camera = null;
    this.domElement = null;
    this.selectableMeshes = [];
  }
}
